import { useState } from 'react';
import type React from 'react';

import { cn } from '@change/ds-utils';

import { useI18n } from '@change-corgi/core-react/i18n';

import { isMotionReduced } from '#src/app/shared/utils/accessibility';

import { PulseDot } from './components/PulseDot';
import type { SpikingPetitionBannerTranslationOverrides } from './hooks/useSpikingPetitionBannerTranslations';
import { useSpikingPetitionBannerTranslations } from './hooks/useSpikingPetitionBannerTranslations';

/**
 * The whole bar is a link; the close button is a sibling, not a child, so it never navigates.
 * Dismissal plays the exit animation first, then calls `onDismissed` when it ends.
 */
export const SpikingPetitionBannerBar: React.FC<Props> = ({
	href,
	signatureCount,
	petitionTitle,
	translationOverrides,
	onClick,
	onClose,
	onDismissed,
}) => {
	const { localizeNumber } = useI18n();
	const t = useSpikingPetitionBannerTranslations(translationOverrides);
	const [leaving, setLeaving] = useState(false);

	const handleClose = () => {
		onClose();
		if (isMotionReduced()) {
			onDismissed();
		} else {
			setLeaving(true);
		}
	};

	const replacements = {
		count: (
			<strong className="font-semibold">
				{t.peopleCount(signatureCount, { countLocalized: localizeNumber(signatureCount) })}
			</strong>
		),
		title: <strong className="font-semibold">{petitionTitle}</strong>,
		timeRange: <span>{t.timeRange()}</span>,
		// Part of the message, not appended after it, so translators can place it.
		cta: <span className="font-semibold underline underline-offset-[3px]">{t.cta()}</span>,
	};

	return (
		<div
			className={cn(
				'border-stroke-weaker bg-background-raised overflow-hidden border-b motion-reduce:animate-none',
				leaving ? 'animate-spike-banner-out' : 'animate-spike-banner-in',
			)}
			onAnimationEnd={leaving ? onDismissed : undefined}
			data-qa="spiking-petition-banner"
		>
			<div className="mx-auto flex max-w-[1200px] items-center">
				<a
					href={href}
					onClick={onClick}
					className="text-typography-strong hover:bg-fill-hover hover:text-typography-strong flex min-w-0 flex-1 items-center gap-2.5 py-3 pr-1 pl-4 no-underline md:pr-2 md:pl-6"
					data-qa="spiking-petition-banner-link"
				>
					<PulseDot />
					<span className="line-clamp-2 min-w-0 flex-1 text-sm leading-5 text-pretty md:line-clamp-1">
						{t.messageHtml(signatureCount, replacements)}
					</span>
				</a>
				<button
					type="button"
					onClick={handleClose}
					aria-label={t.dismissLabel()}
					className="text-icon-weak hover:bg-fill-hover mr-1 flex size-11 flex-none cursor-pointer items-center justify-center rounded-full border-0 bg-transparent md:mr-3"
					data-qa="spiking-petition-banner-close"
				>
					<svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true" focusable="false">
						<path d="M3 3l8 8M11 3l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
					</svg>
				</button>
			</div>
		</div>
	);
};

type Props = Readonly<{
	href: string;
	signatureCount: number;
	petitionTitle: string;
	// Per-locale FCM translation overrides; each field falls back to the regular translation.
	translationOverrides: SpikingPetitionBannerTranslationOverrides;
	// Link clicked.
	onClick: () => void;
	// Close button clicked, before the exit animation starts.
	onClose: () => void;
	// The banner is finished leaving: after the exit animation, or at once under reduced motion.
	onDismissed: () => void;
}>;
